// ─── components/TaskReminders.js ────────────────────────────────────────────
// Right-side notification panel showing today's pending tasks.
// - Opens automatically once per browser session if there are tasks today
// - Pop-spring open animation (scale up with a small bounce), scale-down on close
// - When closed, a bell button with a count badge stays on the right edge
// - Tasks refresh every 5 minutes
// - Tasks whose time has passed are highlighted in red
import React, { useState, useEffect, useCallback, useRef } from "react";
import { getMyTasks, completeTask } from "../api";

const SEEN_KEY = "aranyaka_task_reminder_seen";
const REFRESH_MS = 5 * 60 * 1000;
const CLOSE_MS = 250;

const ANIMATION_CSS = `
@keyframes trPopIn {
  0%   { opacity: 0; transform: scale(.5); }
  60%  { opacity: 1; transform: scale(1.07); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes trPopOut {
  from { opacity: 1; transform: scale(1); }
  to   { opacity: 0; transform: scale(.6); }
}
@media (prefers-reduced-motion: reduce) {
  .tr-anim { animation: none !important; }
}
`;

const getTodayKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isTimePassed = (task) => {
  if (!task.due_time) return false;

  const [h, m] = String(task.due_time).slice(0, 5).split(":");
  const now = new Date();

  return (
    now.getHours() > Number(h) ||
    (now.getHours() === Number(h) && now.getMinutes() >= Number(m))
  );
};

export default function TaskReminders() {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const closeTimerRef = useRef(null);

  const loadTodayTasks = useCallback(async () => {
    try {
      const response = await getMyTasks();
      const all = Array.isArray(response.data) ? response.data : [];
      const todayKey = getTodayKey();

      const today = all
        .filter(
          (task) =>
            task.due_date &&
            String(task.due_date).slice(0, 10) === todayKey
        )
        .sort((a, b) =>
          String(a.due_time || "99:99").localeCompare(
            String(b.due_time || "99:99")
          )
        );

      setTasks(today);

      // First load in this session: open the panel if there are tasks today
      if (today.length > 0 && !sessionStorage.getItem(SEEN_KEY)) {
        sessionStorage.setItem(SEEN_KEY, "1");
        setOpen(true);
      }
    } catch (err) {
      console.error("TASK REMINDERS LOAD ERROR:", err);
    }
  }, []);

  useEffect(() => {
    loadTodayTasks();

    const timer = window.setInterval(loadTodayTasks, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [loadTodayTasks]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Play the scale-down animation first, then actually close
  const closePanel = () => {
    if (closing) return;

    setClosing(true);

    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, CLOSE_MS);
  };

  const markDone = async (taskId) => {
    try {
      setBusyId(taskId);
      await completeTask(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (err) {
      console.error("TASK REMINDER DONE ERROR:", err);
      alert(
        err.response?.data?.error || "Could not complete task"
      );
    } finally {
      setBusyId(null);
    }
  };

  const openLead = (leadId) => {
    if (!leadId) return;
    window.open(`${window.location.origin}/leads/${leadId}`, "_blank");
  };

  // Nothing to show if there are no tasks today
  if (tasks.length === 0) return null;

  const overdueCount = tasks.filter(isTimePassed).length;

  // ── Collapsed state: bell button only ──
  if (!open) {
    return (
      <>
        <style>{ANIMATION_CSS}</style>

        <button
          type="button"
          className="tr-anim"
          onClick={() => setOpen(true)}
          title="Today's tasks"
          style={{
            position: "fixed",
            right: 0,
            top: 60,
            zIndex: 9000,
            border: "none",
            background: "#1D9E75",
            color: "#fff",
            padding: "7px 12px",
            borderRadius: "10px 0 0 10px",
            boxShadow: "-3px 3px 12px rgba(0,0,0,.2)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 8,
            transformOrigin: "100% 50%",
            animation: "trPopIn .45s ease both",
          }}
        >
          <span style={{ fontSize: 16 }}>🔔</span>
          Today's Tasks
          <span
            style={{
              background: overdueCount > 0 ? "#DC2626" : "#fff",
              color: overdueCount > 0 ? "#fff" : "#1D9E75",
              borderRadius: 20,
              padding: "1px 8px",
              fontSize: 12,
            }}
          >
            {tasks.length}
          </span>
        </button>
      </>
    );
  }

  // ── Expanded panel ──
  return (
    <>
      <style>{ANIMATION_CSS}</style>

      <div
        className="tr-anim"
        style={{
          position: "fixed",
          right: 16,
          top: 128,
          width: 320,
          maxWidth: "92vw",
          maxHeight: "calc(100vh - 150px)",
          zIndex: 9000,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 12px 40px rgba(0,0,0,.22)",
          border: "1px solid rgba(0,0,0,.08)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transformOrigin: "100% 0",
          animation: closing
            ? `trPopOut ${CLOSE_MS}ms ease forwards`
            : "trPopIn .5s ease both",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            padding: "12px 14px",
            background: "#1D9E75",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              🔔 Today's Tasks ({tasks.length})
            </div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              {overdueCount > 0
                ? `${overdueCount} overdue`
                : "All tasks are upcoming"}
            </div>
          </div>

          <button
            type="button"
            onClick={closePanel}
            title="Minimize"
            style={{
              border: "none",
              background: "transparent",
              color: "#fff",
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        {/* LIST */}
        <div style={{ overflowY: "auto", padding: 8 }}>
          {tasks.map((task) => {
            const late = isTimePassed(task);

            return (
              <div
                key={task.id}
                style={{
                  border: "1px solid",
                  borderColor: late ? "#FECACA" : "#E5E7EB",
                  background: late ? "#FEF2F2" : "#fff",
                  borderRadius: 8,
                  padding: "8px 10px",
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: late ? "#DC2626" : "#1D9E75",
                    }}
                  >
                    {task.due_time
                      ? String(task.due_time).slice(0, 5)
                      : "All day"}
                  </span>

                  {late && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#DC2626",
                      }}
                    >
                      OVERDUE
                    </span>
                  )}
                </div>

                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {task.lead_name || "No Lead"}
                </div>

                {task.lead_phone && (
                  <div style={{ fontSize: 11, color: "#6B7280" }}>
                    {task.lead_phone}
                  </div>
                )}

                <div
                  style={{
                    fontSize: 12,
                    color: "#374151",
                    marginTop: 2,
                    wordBreak: "break-word",
                  }}                
                >
                  {task.title}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    marginTop: 8,
                  }}
                >
                  
                  

                  {task.lead_id && (
                    <button
                      type="button"
                      onClick={() => openLead(task.lead_id)}
                      style={{
                        flex: 1,
                        border: "1px solid #378ADD",
                        background: "#fff",
                        color: "#378ADD",
                        borderRadius: 6,
                        padding: "5px 0",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Open ↗
                    </button>
                  )}

                  <button
                    type="button"
                    disabled={busyId === task.id}
                    onClick={() => markDone(task.id)}
                    style={{
                      flex: 1,
                      border: "none",
                      background: "#1D9E75",
                      color: "#fff",
                      borderRadius: 6,
                      padding: "5px 0",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor:
                        busyId === task.id ? "not-allowed" : "pointer",
                      opacity: busyId === task.id ? 0.7 : 1,
                    }}
                  >
                    {busyId === task.id ? "..." : "Done ✓"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}